/**
 * As fotografias do caso, lidas ao montar e relidas enquanto a classificação das imagens ou a
 * conferência facial automática rodam em segundo plano (E123.F3, GUS-128).
 *
 * Três leituras que já existiam, juntas: as fotos do caso (a eleita de cada grupo, com a origem),
 * a conferência (o parecer, e se ela está em curso) e as candidatas (a classificação em curso, os
 * nomes dos documentos, as leituras propostas). A próxima consulta só é agendada depois que a
 * anterior volta — nunca empilha pedidos (E113.F1.T03) —, e uma falha de releitura reagenda a
 * seguinte em vez de parar a espera.
 */
import type { AxiosInstance } from 'axios';
import { useCallback, useEffect, useState } from 'react';

import { listarCandidatasDeImagem } from '@/lib/candidatas_de_imagem';
import { lerConferenciaFacial } from '@/lib/conferencia_facial';
import { precisaReler, type FotografiasDoCasoData } from '@/lib/fotografias_do_caso';
import { listarFotosDoSignatario } from '@/lib/fotos_do_signatario';

export const INTERVALO_DAS_FOTOGRAFIAS_MS = 3000;

async function lerTudo(client: AxiosInstance, caseId: string): Promise<FotografiasDoCasoData> {
  const [fotos, conferencia, candidatas] = await Promise.all([
    listarFotosDoSignatario(client, caseId),
    lerConferenciaFacial(client, caseId),
    listarCandidatasDeImagem(client, caseId),
  ]);
  return { fotos, conferencia, candidatas };
}

export interface FotografiasDoCaso {
  dados: FotografiasDoCasoData | null;
  erro: boolean;
  recarregar: () => Promise<void>;
}

export function useFotografiasDoCaso(client: AxiosInstance, caseId: string): FotografiasDoCaso {
  const [dados, setDados] = useState<FotografiasDoCasoData | null>(null);
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  const recarregar = useCallback(async (): Promise<void> => {
    setDados(await lerTudo(client, caseId));
  }, [client, caseId]);

  useEffect(() => {
    let cancelado = false;
    setDados(null);
    setErro(false);
    void lerTudo(client, caseId)
      .then((lidos) => {
        if (!cancelado) setDados(lidos);
      })
      .catch(() => {
        if (!cancelado) setErro(true);
      });
    return () => {
      cancelado = true;
    };
  }, [client, caseId]);

  const reler = precisaReler(dados);
  useEffect(() => {
    if (!reler) return undefined;
    let cancelado = false;
    const agendada = window.setTimeout(() => {
      void lerTudo(client, caseId)
        .then((lidos) => {
          if (!cancelado) setDados(lidos);
        })
        .catch(() => {
          if (!cancelado) setTentativa((n) => n + 1);
        });
    }, INTERVALO_DAS_FOTOGRAFIAS_MS);
    return () => {
      cancelado = true;
      window.clearTimeout(agendada);
    };
  }, [client, caseId, dados, reler, tentativa]);

  return { dados, erro, recarregar };
}
